import { CheckOutlined, DeleteOutlined, InfoCircleOutlined, PlusOutlined, ProfileOutlined } from "@ant-design/icons";
import {
  Button,
  Empty,
  Flex,
  Grid,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from "antd";
import axios, { isCancel } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

import CanonicalTagSelect from "./CanonicalTagSelect";
import {
  CANONICAL_TAGS_ENDPOINT_PATH,
  METADATA_FIELDS_ENDPOINT_PATH,
  REVIEW_STATE_TABS,
  TAG_DOMAIN_ENUM,
  TAG_DOMAIN_FILTER_OPTIONS,
  TAG_SCREEN_HELP_TEXT,
  TAGS_API_BASE,
  TAGS_PAGINATION_CONFIG
} from "./helper";
import MappingFormModal from "./MappingFormModal";
import MetadataChips from "./MetadataChips";
import MetadataModal from "./MetadataModal";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";

const { Text } = Typography;

/** Width the table gives a column, so `scroll.x` can be summed from the columns actually rendered. */
const COLUMN_WIDTHS = Object.freeze({
  tagDomain: 180,
  sourceTag: 200,
  canonicalTag: 300,
  isReviewed: 160,
  metadata: 280,
  actions: 200
});

/**
 * Starts a request that supersedes the previous one under the same key: the
 * previous request is aborted and a fresh controller takes its place.
 *
 * @param {{current: Record<string, AbortController>}} abortControllersRef - Controller of the
 *   latest request per key.
 * @param {string} requestKey - Identifies the request slot, for example one list or one type's options.
 * @returns {AbortController} Its `signal` goes on the request.
 */
const beginRequest = (abortControllersRef, requestKey) => {
  abortControllersRef.current[requestKey]?.abort();
  const abortController = new AbortController();
  abortControllersRef.current[requestKey] = abortController;
  return abortController;
};

/**
 * Reports a failed request once. A request aborted by a newer one is
 * superseded, not failed, and stays silent.
 *
 * @param {Error} error
 */
const handleRequestError = (error) => {
  if (isCancel(error)) {
    return;
  }

  showErrorNotification(error);
};

/** Shown in place of a control an admin without management access cannot use. */
const NO_MANAGEMENT_ACCESS_REASON = "You need management access to change mappings";

/**
 * Says why a row's Approve or Update button is disabled, or nothing when it is enabled.
 *
 * @param {object} params
 * @param {boolean} params.hasManagementAccess
 * @param {boolean} params.isUpdateBlocked - True on a reviewed row whose picker still holds the stored tag.
 * @returns {string|undefined}
 */
const getSaveDisabledReason = ({ hasManagementAccess, isUpdateBlocked }) => {
  if (!hasManagementAccess) {
    return NO_MANAGEMENT_ACCESS_REASON;
  }
  if (isUpdateBlocked) {
    return "Change the canonical tag to update";
  }

  return undefined;
};

/**
 * A column heading with the one sentence of plain English that explains it,
 * behind an info icon.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {string} props.helpText
 */
const ColumnHeading = ({ title, helpText }) => (
  <Space size={4}>
    {title}
    <Tooltip title={helpText}>
      <InfoCircleOutlined style={{ color: "var(--text-secondary)", cursor: "pointer" }} />
    </Tooltip>
  </Space>
);

/**
 * The tag mapping screen: a source's own tag on one side, our tag on the other,
 * and an admin's decision recorded between them.
 *
 * Nothing here belongs to any one product line. Pinning a type turns the screen
 * into that line's own sub-page: its type controls disappear. Every row's
 * metadata renders as chips in one column, labelled from the field list the
 * row's own type declares, so a product line added later needs nothing here.
 *
 * Saving an empty canonical tag is a decision, not an unfinished row: it
 * records that the source's tag has no equivalent of ours, and that is what
 * moves the row out of the awaiting-review tab for good.
 *
 * @param {object} props
 * @param {string} props.ROOT_URL
 * @param {string} [props.pinnedTagDomain] - Fixes the screen to one product line and hides its type controls.
 * @param {boolean} props.hasManagementAccess - False leaves the screen read-only, matching the
 *   admin's `externalProblems.hasManagementAccess` permission.
 */
const TagMappings = ({ ROOT_URL, pinnedTagDomain, hasManagementAccess }) => {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [hasLoadedList, setHasLoadedList] = useState(false);
  const [loadedTabKey, setLoadedTabKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(TAGS_PAGINATION_CONFIG.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState(TAGS_PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);

  const [tagDomainFilter, setTagDomainFilter] = useState(pinnedTagDomain);
  const [activeTabKey, setActiveTabKey] = useState(REVIEW_STATE_TABS[0].key);
  const [searchTerm, setSearchTerm] = useState("");

  const [canonicalTagOptionsByDomain, setCanonicalTagOptionsByDomain] = useState({});
  const [metadataFieldsByDomain, setMetadataFieldsByDomain] = useState({});
  const [canonicalTagDrafts, setCanonicalTagDrafts] = useState({});
  const [savingUniquePublicId, setSavingUniquePublicId] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [metadataRow, setMetadataRow] = useState(null);

  const abortControllersRef = useRef({});

  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const columns = [
    {
      title: <ColumnHeading title="Type" helpText={TAG_SCREEN_HELP_TEXT.tagDomain} />,
      dataIndex: "tagDomainDisplayName",
      key: "tagDomainDisplayName",
      width: COLUMN_WIDTHS.tagDomain,
      hidden: Boolean(pinnedTagDomain),
      responsive: ["md"]
    },
    {
      title: <ColumnHeading title="Source tag" helpText={TAG_SCREEN_HELP_TEXT.sourceTag} />,
      dataIndex: "sourceTag",
      key: "sourceTag",
      width: COLUMN_WIDTHS.sourceTag,
      render: (sourceTag, { isAdminCreated }) =>
        isAdminCreated ? <Text type="secondary">Added by an admin</Text> : sourceTag
    },
    {
      title: <ColumnHeading title="Canonical tag" helpText={TAG_SCREEN_HELP_TEXT.canonicalTag} />,
      key: "canonicalTag",
      width: COLUMN_WIDTHS.canonicalTag,
      render: (_, { uniquePublicId, tagDomain, canonicalTag, isReviewed, isAdminCreated }) => {
        const draftValue = canonicalTagDrafts[uniquePublicId] ?? canonicalTag;

        if (isAdminCreated) {
          return <Text strong>{canonicalTag}</Text>;
        }

        // The row's own type scopes its picker, so a row stays editable while
        // the screen spans every product line.
        return (
          <Space orientation="vertical" size={2} style={{ width: "100%" }}>
            <CanonicalTagSelect
              ROOT_URL={ROOT_URL}
              tagDomain={tagDomain}
              value={draftValue}
              canonicalTagOptions={canonicalTagOptionsByDomain[tagDomain] ?? []}
              disabled={!hasManagementAccess}
              placeholder="Leave empty for no equivalent"
              onChange={(nextCanonicalTag) => handleDraftChange(uniquePublicId, nextCanonicalTag)}
              onCanonicalTagCreated={fetchTypeScopedLists}
            />
            {isReviewed && !draftValue && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                Decided: no equivalent of ours
              </Text>
            )}
          </Space>
        );
      }
    },
    {
      title: <ColumnHeading title="Review state" helpText={TAG_SCREEN_HELP_TEXT.isReviewed} />,
      dataIndex: "isReviewed",
      key: "isReviewed",
      width: COLUMN_WIDTHS.isReviewed,
      align: "center",
      responsive: ["md"],
      render: (isReviewed) =>
        isReviewed ? <Tag color="green">Reviewed</Tag> : <Tag>Awaiting review</Tag>
    },
    {
      title: <ColumnHeading title="Metadata" helpText={TAG_SCREEN_HELP_TEXT.metadata} />,
      key: "metadata",
      width: COLUMN_WIDTHS.metadata,
      render: (_, { tagDomain, metadata }) => (
        <MetadataChips metadata={metadata} metadataFields={metadataFieldsByDomain[tagDomain]} />
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: COLUMN_WIDTHS.actions,
      align: "center",
      render: (_, tag) => {
        const { uniquePublicId, canonicalTag, isReviewed, isAdminCreated } = tag;
        const isRowSaving = savingUniquePublicId === uniquePublicId;
        const isDecisionUnchanged = (canonicalTagDrafts[uniquePublicId] ?? canonicalTag ?? "") === (canonicalTag ?? "");
        const isUpdateBlocked = isReviewed && isDecisionUnchanged;
        const saveDisabledReason = getSaveDisabledReason({ hasManagementAccess, isUpdateBlocked });

        return (
          <Space size={8}>
            {!isAdminCreated && (
              <Tooltip title={saveDisabledReason}>
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={isRowSaving}
                  disabled={Boolean(saveDisabledReason)}
                  onClick={() => handleSaveDecision(tag)}
                >
                  {isReviewed ? "Update" : "Approve"}
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Metadata">
              <Button icon={<ProfileOutlined />} onClick={() => setMetadataRow(tag)} />
            </Tooltip>
            {isAdminCreated && (
              <Popconfirm
                title="Remove this canonical tag?"
                description="It disappears from the picker. Problems already carrying it keep it until they are read again."
                okText="Remove"
                cancelText="Keep"
                onConfirm={() => handleDelete(uniquePublicId)}
              >
                <Tooltip title={hasManagementAccess ? undefined : NO_MANAGEMENT_ACCESS_REASON}>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!hasManagementAccess}
                    loading={isRowSaving}
                  />
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ];

  const visibleColumns = columns.filter(({ hidden }) => !hidden);
  // The type column drops out on a sub-page, so the scroll width has to follow
  // the columns actually rendered.
  const tableScrollWidth = visibleColumns.reduce((totalWidth, { width }) => totalWidth + width, 0);

  const { emptyText: tabEmptyText } = REVIEW_STATE_TABS.find(({ key }) => key === activeTabKey);
  const emptyText = searchTerm ? `No mapping matches "${searchTerm}".` : tabEmptyText;
  const isAwaitingReviewTab = activeTabKey === REVIEW_STATE_TABS[0].key;
  const shouldOfferReviewedTab = isAwaitingReviewTab && !searchTerm;
  const isEmptyStateVisible = hasLoadedList && !isLoading;

  const tabItems = REVIEW_STATE_TABS.map(({ key, label, description }) => ({
    key,
    label: (
      <Space size={6}>
        {key === activeTabKey && key === loadedTabKey ? `${label} (${totalItems.toLocaleString()})` : label}
        <Tooltip title={description}>
          <InfoCircleOutlined style={{ color: "var(--text-secondary)", cursor: "pointer" }} />
        </Tooltip>
      </Space>
    )
  }));

  /**
   * Loads the page of mappings for the active tab, product line filter and
   * search term, and records the total for the pager and the tab label.
   *
   * Only the latest call writes state: a call aborts the one before it. A
   * failure leaves the rows on screen and reports once. The returned promise
   * always resolves, after the response, the failure or the abort.
   *
   * @returns {Promise<void>}
   */
  const fetchTags = useCallback(() => {
    const { isReviewed } = REVIEW_STATE_TABS.find(({ key }) => key === activeTabKey);
    const { signal } = beginRequest(abortControllersRef, "list");

    setIsLoading(true);
    return axios
      .get(`${ROOT_URL}/${TAGS_API_BASE}`, {
        signal,
        params: {
          page: currentPage,
          limit: pageSize,
          tagDomain: tagDomainFilter,
          isReviewed,
          searchTerm: searchTerm || undefined
        }
      })
      .then((response) => {
        const { tags: fetchedTags, pagination } = response.data;
        setTags(fetchedTags);
        setTotalItems(pagination.totalItems);
        setLoadedTabKey(activeTabKey);
        setHasLoadedList(true);
      })
      .catch(handleRequestError)
      .finally(() => {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      });
  }, [ROOT_URL, currentPage, pageSize, tagDomainFilter, activeTabKey, searchTerm]);

  /**
   * Loads the canonical tag options and the metadata field list of every
   * product line on screen, each keyed by type. A later call for the same
   * type aborts the earlier one, and an aborted call writes nothing.
   */
  const fetchTypeScopedLists = useCallback(() => {
    const tagDomainsOnScreen = pinnedTagDomain ? [pinnedTagDomain] : Object.values(TAG_DOMAIN_ENUM);
    tagDomainsOnScreen.forEach((tagDomain) => {
      const { signal: optionsSignal } = beginRequest(abortControllersRef, `canonical-tags:${tagDomain}`);
      axios
        .get(`${ROOT_URL}/${TAGS_API_BASE}/${CANONICAL_TAGS_ENDPOINT_PATH}`, {
          signal: optionsSignal,
          params: { tagDomain }
        })
        .then((response) => {
          const { canonicalTags } = response.data;
          setCanonicalTagOptionsByDomain((previousOptions) => ({
            ...previousOptions,
            [tagDomain]: canonicalTags.map((canonicalTag) => ({ label: canonicalTag, value: canonicalTag }))
          }));
        })
        .catch(handleRequestError);

      const { signal: fieldsSignal } = beginRequest(abortControllersRef, `metadata-fields:${tagDomain}`);
      axios
        .get(`${ROOT_URL}/${TAGS_API_BASE}/${METADATA_FIELDS_ENDPOINT_PATH}`, {
          signal: fieldsSignal,
          params: { tagDomain }
        })
        .then((response) => {
          const { metadataFields } = response.data;
          setMetadataFieldsByDomain((previousFields) => ({ ...previousFields, [tagDomain]: metadataFields }));
        })
        .catch(handleRequestError);
    });
  }, [ROOT_URL, pinnedTagDomain]);

  /**
   * Starts a new view of the list: back to the first page with no unsaved
   * picks carried over. Called in the same handler as the change that caused
   * it, so React batches both into one render and the list is fetched once.
   */
  const resetView = () => {
    setCurrentPage(TAGS_PAGINATION_CONFIG.DEFAULT_PAGE);
    setCanonicalTagDrafts({});
  };

  /**
   * Switches the review tab and starts a new view.
   *
   * @param {string} tabKey - A `REVIEW_STATE_TABS` key.
   */
  const handleTabChange = (tabKey) => {
    setActiveTabKey(tabKey);
    resetView();
  };

  /**
   * Narrows the list to one type, or to all types when cleared, and starts a new view.
   *
   * @param {string} [tagDomain] - Undefined when the filter was cleared.
   */
  const handleTagDomainFilterChange = (tagDomain) => {
    setTagDomainFilter(tagDomain);
    resetView();
  };

  /**
   * Applies the search box's text and starts a new view.
   *
   * @param {string} nextSearchTerm - Empty when the box was cleared.
   */
  const handleSearch = (nextSearchTerm) => {
    setSearchTerm(nextSearchTerm.trim());
    resetView();
  };

  /**
   * Holds the canonical tag picked for one row until the admin saves it.
   *
   * @param {string} uniquePublicId
   * @param {string} canonicalTag - Empty when the picker was cleared.
   */
  const handleDraftChange = (uniquePublicId, canonicalTag) =>
    setCanonicalTagDrafts((previousDrafts) => ({
      ...previousDrafts,
      [uniquePublicId]: canonicalTag
    }));

  /**
   * Records the decision on one row: whatever the picker currently holds,
   * including nothing, since an empty canonical tag on a saved row is the
   * decision "this source tag has no equivalent of ours".
   *
   * @param {{uniquePublicId: string, canonicalTag: string}} tag
   */
  const handleSaveDecision = ({ uniquePublicId, canonicalTag }) => {
    const draftCanonicalTag = canonicalTagDrafts[uniquePublicId] ?? canonicalTag;

    setSavingUniquePublicId(uniquePublicId);
    axios
      .patch(`${ROOT_URL}/${TAGS_API_BASE}/${uniquePublicId}`, {
        canonicalTag: draftCanonicalTag,
        isReviewed: true
      })
      .then(() => {
        showSuccessNotification("Mapping saved successfully!");
        return fetchTags();
      })
      .then(() =>
        // The draft goes only once the refetched row carries the saved value, so the picker never shows the old tag in between.
        setCanonicalTagDrafts((previousDrafts) => {
          const { [uniquePublicId]: _savedDraft, ...remainingDrafts } = previousDrafts;
          return remainingDrafts;
        })
      )
      .catch(handleRequestError)
      .finally(() => setSavingUniquePublicId(null));
  };

  /**
   * Removes a canonical tag an admin added. A source's own tag is refused by
   * the backend, because the next read of that source brings it straight back.
   *
   * @param {string} uniquePublicId
   */
  const handleDelete = (uniquePublicId) => {
    setSavingUniquePublicId(uniquePublicId);
    axios
      .delete(`${ROOT_URL}/${TAGS_API_BASE}/${uniquePublicId}`)
      .then(() => {
        showSuccessNotification("Canonical tag removed successfully!");
        return fetchTags();
      })
      .catch(handleRequestError)
      .finally(() => setSavingUniquePublicId(null));
  };

  /** Closes the create modal and reloads the list and the type-scoped lists. */
  const handleCreated = () => {
    setIsCreateModalOpen(false);
    fetchTags();
    fetchTypeScopedLists();
  };

  /** Closes the metadata modal and reloads the list. */
  const handleMetadataSaved = () => {
    setMetadataRow(null);
    fetchTags();
  };

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    fetchTypeScopedLists();
  }, [fetchTypeScopedLists]);

  useEffect(() => {
    const abortControllers = abortControllersRef.current;
    return () => Object.values(abortControllers).forEach((abortController) => abortController.abort());
  }, []);

  return (
    <div style={{ padding: clamp(12, 24) }}>
      <Tabs
        activeKey={activeTabKey}
        items={tabItems}
        onChange={handleTabChange}
      />

      <Flex gap={8} wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          style={{ flex: "1 1 320px" }}
          placeholder="Search source or canonical tag"
          onSearch={handleSearch}
        />
        {!pinnedTagDomain && (
          <Select
            placeholder="All types"
            allowClear
            style={{ minWidth: 220 }}
            options={TAG_DOMAIN_FILTER_OPTIONS}
            value={tagDomainFilter}
            onChange={handleTagDomainFilterChange}
          />
        )}
        <Tooltip title={hasManagementAccess ? undefined : NO_MANAGEMENT_ACCESS_REASON}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!hasManagementAccess}
            onClick={() => setIsCreateModalOpen(true)}
          >
            Add mapping
          </Button>
        </Tooltip>
      </Flex>

      <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        {totalItems} mapping{totalItems === 1 ? "" : "s"} matching current filters
      </Text>

      <Table
        rowKey="uniquePublicId"
        columns={visibleColumns}
        dataSource={tags}
        loading={isLoading}
        bordered
        size={isMobileView ? "small" : "middle"}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalItems,
          showSizeChanger: !isMobileView,
          simple: isMobileView,
          pageSizeOptions: TAGS_PAGINATION_CONFIG.PAGE_SIZE_OPTIONS,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }
        }}
        locale={{
          emptyText: isEmptyStateVisible ? (
            <Empty description={emptyText}>
              {shouldOfferReviewedTab && (
                <Button onClick={() => handleTabChange(REVIEW_STATE_TABS[1].key)}>View reviewed mappings</Button>
              )}
            </Empty>
          ) : (
            <div style={{ minHeight: 120 }} />
          )
        }}
        scroll={{ x: isMobileView ? "max-content" : tableScrollWidth }}
      />

      {isCreateModalOpen && (
        <MappingFormModal
          ROOT_URL={ROOT_URL}
          isOpen={isCreateModalOpen}
          fixedTagDomain={tagDomainFilter}
          canonicalTagOptionsByDomain={canonicalTagOptionsByDomain}
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={handleCreated}
          onCanonicalTagCreated={fetchTypeScopedLists}
        />
      )}

      {metadataRow && (
        <MetadataModal
          ROOT_URL={ROOT_URL}
          tag={metadataRow}
          cachedMetadataFields={metadataFieldsByDomain[metadataRow.tagDomain]}
          hasManagementAccess={hasManagementAccess}
          onClose={() => setMetadataRow(null)}
          onSaved={handleMetadataSaved}
        />
      )}
    </div>
  );
};

export default TagMappings;
